import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const PLACE_LABELS = {
  1: '1st Place', 2: '2nd Place', 3: '3rd Place', 4: '4th Place',
  5: '5th Place', 6: '6th Place', 7: '7th Place', 8: '8th Place',
  9: '9th Place', 10: '10th Place', last: 'Last Place',
};

const PLACE_MEDALS = {
  1: '🥇', 2: '🥈', 3: '🥉', last: '💀',
};

export default function PayoutPage() {
  const api = useApi();
  const [config, setConfig] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const [cfg, lb] = await Promise.all([
          fetch(`${API_BASE}/payout-config`).then(r => r.json()),
          api.getLeaderboard(),
        ]);
        setConfig(cfg);
        setLeaderboard(lb);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return (
    <div className="loading-container">
      <div className="spinner"></div>
      <div className="loading-text">Loading payouts...</div>
    </div>
  );

  if (!config?.enabled) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">POOL <span>PAYOUTS</span></h1>
      </div>
      <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--gray)' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>🏆</div>
        <p style={{ fontFamily: 'var(--font-condensed)', fontSize: '1.1rem' }}>
          Payout information is not currently available.
        </p>
      </div>
    </div>
  );

  // Determine if tournament is over (championship game has a winner)
  const champEntry = leaderboard?.champTotal !== null && leaderboard?.champTotal !== undefined;
  const tournamentOver = champEntry;

  // Build the list of payouts to display (non-zero amounts only)
  const payouts = config.payouts || {};
  const placeOrder = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  const activePayouts = placeOrder.filter(p => Number(payouts[p]) > 0);
  const hasLast = Number(payouts.last) > 0;

  const entries = leaderboard?.entries || [];
  const totalEntries = entries.length;

  // Get bracket/user for a given rank position
  function getEntryForRank(rank) {
    if (!tournamentOver) return null;
    return entries.find(e => e.rank === rank) || null;
  }

  function getLastPlaceEntry() {
    if (!tournamentOver || entries.length === 0) return null;
    // Last by rank (highest rank number, accounting for tiebreakers)
    const maxRank = Math.max(...entries.map(e => e.rank));
    const lastEntries = entries.filter(e => e.rank === maxRank);
    return lastEntries[lastEntries.length - 1] || null;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">POOL <span>PAYOUTS</span></h1>
        <p className="page-subtitle">
          {tournamentOver
            ? 'Tournament complete — final payout positions'
            : `Payout structure · ${totalEntries} bracket${totalEntries !== 1 ? 's' : ''} entered`}
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {!tournamentOver && (
        <div className="alert alert-info" style={{ marginBottom: 24 }}>
          Current standings will be shown here once the tournament is complete.
        </div>
      )}

      <div className="card">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th style={{ width: 140 }}>Place</th>
              <th style={{ textAlign: 'right', width: 120 }}>Payout</th>
              {tournamentOver && <th>Winner</th>}
              {tournamentOver && <th style={{ textAlign: 'right' }}>Score</th>}
            </tr>
          </thead>
          <tbody>
            {activePayouts.map(place => {
              const entry = getEntryForRank(place);
              const medal = PLACE_MEDALS[place] || '🏅';
              return (
                <tr key={place}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: '1.4rem' }}>{medal}</span>
                      <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem' }}>
                        {PLACE_LABELS[place]}
                      </span>
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--green)' }}>
                      ${Number(payouts[place]).toLocaleString()}
                    </span>
                  </td>
                  {tournamentOver && (
                    <td>
                      {entry ? (
                        <div>
                          <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem', color: 'var(--cream)' }}>
                            {entry.bracketName}
                          </div>
                          <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gray)' }}>
                            {entry.userName}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--gray)', fontFamily: 'var(--font-condensed)' }}>—</span>
                      )}
                    </td>
                  )}
                  {tournamentOver && (
                    <td style={{ textAlign: 'right' }}>
                      {entry ? <span className="score-value">{entry.score}</span> : '—'}
                    </td>
                  )}
                </tr>
              );
            })}

            {hasLast && (() => {
              const lastEntry = getLastPlaceEntry();
              return (
                <tr key="last" style={{ borderTop: '2px solid var(--slate)' }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: '1.4rem' }}>💀</span>
                      <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem' }}>
                        Last Place
                      </span>
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: '#e67e22' }}>
                      ${Number(payouts.last).toLocaleString()}
                    </span>
                  </td>
                  {tournamentOver && (
                    <td>
                      {lastEntry ? (
                        <div>
                          <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, fontSize: '1rem', color: 'var(--cream)' }}>
                            {lastEntry.bracketName}
                          </div>
                          <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.85rem', color: 'var(--gray)' }}>
                            {lastEntry.userName}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--gray)', fontFamily: 'var(--font-condensed)' }}>—</span>
                      )}
                    </td>
                  )}
                  {tournamentOver && (
                    <td style={{ textAlign: 'right' }}>
                      {lastEntry ? <span className="score-value">{lastEntry.score}</span> : '—'}
                    </td>
                  )}
                </tr>
              );
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}
